import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { refreshSession } from '@/api/client';
import { tokenStore } from '@/api/tokenStore';
import { onBillingLocked } from '@/features/billing/lib/billingLock';

import * as authApi from './api/auth';
import type { CurrentUser } from './api/auth';
import {
  AuthContext,
  PLATFORM_OWNER_ROLE,
  holdsPermission,
  type AuthContextValue,
  type AuthStatus,
} from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [current, setCurrent] = useState<CurrentUser | null>(null);
  const queryClient = useQueryClient();

  const loadCurrentUser = useCallback(async () => {
    try {
      const me = await authApi.fetchCurrentUser();
      setCurrent(me);
      setStatus('authenticated');
    } catch {
      setCurrent(null);
      setStatus('anonymous');
    }
  }, []);

  // On boot the access token is gone (it only ever lived in memory), but the
  // refresh cookie may still be valid - so try once before deciding the visitor
  // is anonymous.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = tokenStore.get() ?? (await refreshSession());
      if (cancelled) return;
      if (!token) {
        setStatus('anonymous');
        return;
      }
      await loadCurrentUser();
    })();

    return () => {
      cancelled = true;
    };
  }, [loadCurrentUser]);

  // The API client saw a `402 BILLING_LOCKED`: the lock is read again from
  // `/auth/me` so the route guard kicks in on the next render. Once, not once
  // per refused request - a dashboard that just locked fails a dozen queries
  // at a time, and the first re-read answers for all of them.
  const locked = useRef(false);
  locked.current = current?.billing_lock != null;
  useEffect(
    () =>
      onBillingLocked(() => {
        void queryClient.invalidateQueries({ queryKey: ['me'] });
        if (!locked.current) void loadCurrentUser();
      }),
    [queryClient, loadCurrentUser],
  );

  const signIn = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      await authApi.login(email, password, captchaToken);
      // Re-read from `/auth/me` rather than building the session from the login
      // response: the login payload does not say whether this password was set by
      // somebody else, and guessing false would drop the caller into a dashboard
      // whose every request is refused.
      await loadCurrentUser();
    },
    [loadCurrentUser],
  );

  const signOut = useCallback(async () => {
    await authApi.logout();
    setCurrent(null);
    setStatus('anonymous');
    // Cached responses belong to the account that just left.
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: current?.user ?? null,
      permissions: current?.permissions ?? [],
      hasBusiness: current?.has_business ?? false,
      isPlatformStaff: (current?.permissions ?? []).some((permission) =>
        permission.startsWith('PLATFORM_'),
      ),
      isPartner: (current?.permissions ?? []).some((permission) =>
        permission.startsWith('PARTNER_'),
      ),
      isPlatformOwner: current?.user.role === PLATFORM_OWNER_ROLE,
      mustChangePassword: current?.must_change_password ?? false,
      billingLock: current?.billing_lock ?? null,
      can: (permission: string) => holdsPermission(current?.permissions ?? [], permission),
      signIn,
      signOut,
      reload: loadCurrentUser,
    }),
    [status, current, signIn, signOut, loadCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
